import { dayjs, formatDate, DATE } from '@koob/dates';
import { useTranslation } from 'react-i18next';
import { getSetting, getSettingLabel } from '@koob/utils';
import ExportInaccessibleBadge from './ExportInaccessibleBadge';
import ExportImagePlaceholder, {
  useImageFallback,
} from './ExportImagePlaceholder';
import { getDayStartPosition, formatPositionLabel } from '../dayLabels';

export default function ExportDayHeader({
  trip,
  index,
  program,
  experience,
  settings,
  compact = false,
  nightLabel,
}) {
  const { t } = useTranslation('trips');
  const daysSection = getSetting(settings, 'days');
  const podFields = getSetting(
    daysSection?.sections,
    'program-of-the-day',
  )?.fields;
  const dayLabel = getSettingLabel(podFields, 'pod-day-number') || 'DAY';
  const photoUnavailableLabel =
    getSettingLabel(podFields, 'pod-photo-unavailable') ||
    t('export.photoUnavailable');
  const startPosition = getDayStartPosition(program, experience);
  const locationCaption =
    startPosition?.name ?? formatPositionLabel(startPosition);

  const dayImageUrl =
    program?.picture?.imageUrl ??
    experience?.pictures?.sort((a, b) => a.isMainPicture - b.isMainPicture)?.[0]
      ?.imageUrl;

  const [imageBroken, handleImageError] = useImageFallback();
  // A day with a program keeps its photo block, a placeholder when the photo
  // is missing or fails to load.
  const showImage = !compact && !!(program || experience);
  const hasPhoto = !!dayImageUrl && !imageBroken;

  const caption = (
    <div className={showImage ? 'col-span-4 relative' : ''}>
      {showImage && (
        <div
          aria-hidden
          className="pointer-events-none absolute -left-2 top-5 hidden select-none text-[90px] font-bold leading-none text-[var(--td-ink)] opacity-10 md:block"
          style={{ fontFamily: 'var(--td-font-display)' }}
        >
          {String(index + 1).padStart(2, '0')}
        </div>
      )}
      {locationCaption && (
        <div className="relative text-2xs font-semibold tracking-widest uppercase text-[var(--td-primary)]">
          {locationCaption}
        </div>
      )}
      <div className="relative mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <div
          className={`${compact ? 'text-[22px]' : 'text-3xl'} leading-tight`}
          style={{ fontFamily: 'var(--td-font-display)' }}
        >
          {program?.title ?? experience?.name}
        </div>
        {experience?.isAccessible === false && <ExportInaccessibleBadge />}
      </div>
    </div>
  );

  return (
    <div>
      <div className="text-2xs font-semibold tracking-widest uppercase">
        <span className="text-[var(--td-primary)]">
          {dayLabel} {String(index + 1).padStart(2, '0')}
        </span>
        {!trip?.template && (
          <>
            <span className="mx-2 opacity-30">·</span>
            <span>
              {formatDate(dayjs(trip?.startDate).add(index, 'days'), DATE)}
            </span>
          </>
        )}
        {nightLabel && (
          <>
            <span className="mx-2 opacity-30">·</span>
            <span className="opacity-70">{nightLabel}</span>
          </>
        )}
      </div>

      {showImage ? (
        <div className="mt-3 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-8">
            {hasPhoto ? (
              <img
                className="aspect-[16/8] w-full rounded-sm object-cover shadow-lg"
                crossOrigin="anonymous"
                src={dayImageUrl}
                alt=""
                onError={handleImageError}
              />
            ) : (
              <ExportImagePlaceholder
                label={photoUnavailableLabel}
                className="aspect-[16/8] w-full rounded-sm shadow-lg"
              />
            )}
          </div>
          {caption}
        </div>
      ) : (
        <div className="mt-3">{caption}</div>
      )}
    </div>
  );
}
